/**
 * The user's measured profile, and the data-maturity level that decides which
 * blocks a screen is allowed to render.
 *
 * Two rules from the handoff drive everything here:
 *   1. No number → no colour. A direction with no completed pass has
 *      `score: null`, never a guess off the routing block.
 *   2. Numbers are the user's own. Anything derived from reports stays at zero
 *      until a report actually exists.
 */
import React from 'react';
import { supabase } from '../lib/supabase';
import { mergeTrackResults, TrackSubmission } from '../utils/scoringV3';
import { loadQuizPages } from '../utils/loadQuizPages';
import { Lang } from '../i18n/detectLanguage';
import { AppContent } from './content';
import { earnedFrom, levelOf, LEVEL_STEP } from './points';
import { useTasks } from './tasks';
import { DIR_ORDER, Polarity, effectiveStrength } from './tokens';

/** Which blocks may render. Each level is a superset of the ones before it. */
export type Maturity = 'empty' | 'D0' | 'D7' | 'D30' | 'D90';

export interface DirState {
    code: string;
    /** A completed deep pass exists for this direction. */
    deep: boolean;
    /** When THIS direction's pass was submitted — each track keeps its own date. */
    testedAt: Date | null;
    /** 0–100, or null when not measured. Never fabricated. */
    score: number | null;
    /** Rank among measured directions, 1 = strongest. Null when not measured. */
    rank: number | null;
    /** Patterns found in this direction, strongest-need first. */
    patterns: PatState[];
    /** Points earned into this direction from completed tasks. */
    earned: number;
    /** Points earned into this direction per weekday of the current week, Monday first. Empty until a report exists. */
    trend: number[];
    delta: number | null;
}

export interface PatState {
    code: string;
    name: string;
    sub: string;
    pol: Polarity;
    dim: string;
    /** 0–100 strength, or null when the owning direction was never taken. */
    pct: number | null;
    /** Points earned into this pattern from completed tasks. */
    earned: number;
    /** Percent of the index this pattern accounts for. D30+ only. */
    impact: number | null;
    isNew: boolean;
}

export interface Profile {
    maturity: Maturity;
    /** Index base — from the test. Moves only on a retake. */
    base: number | null;
    /** Every point earned from completed tasks. */
    dynamic: number;
    /**
     * The measured index, 0–100. Null before the first test.
     *
     * Deliberately still the base alone: `dynamic` is what the work has added
     * since, and the two are shown side by side rather than blended, so a number
     * that was measured never quietly becomes a number that was earned. Moving
     * the score from reports needs a scoring rule, not an arithmetic guess.
     */
    index: number | null;
    level: number;
    xpToNext: number;
    /** 0–1 progress through the current level, for the bar. */
    levelPct: number;
    directions: DirState[];
    patterns: PatState[];
    measuredTracks: string[];
    reports: number;
    streak: number;
    /** Points per weekday, oldest first. Empty until reports exist. */
    week: number[];
    testedAt: Date | null;
    loading: boolean;
    error: Error | null;
}

const EMPTY: Omit<Profile, 'loading' | 'error'> = {
    maturity: 'empty',
    base: null,
    dynamic: 0,
    index: null,
    level: 1,
    xpToNext: LEVEL_STEP,
    levelPct: 0,
    directions: [],
    patterns: [],
    measuredTracks: [],
    reports: 0,
    streak: 0,
    week: [0, 0, 0, 0, 0, 0, 0],
    testedAt: null,
};

/**
 * Maturity from what actually exists, not from elapsed time. A user who took the
 * test three months ago but never reported is still D0 — there is nothing to
 * draw a trend from.
 */
export function maturityOf(opts: { measured: number; reports: number; daysOfHistory: number }): Maturity {
    if (opts.measured === 0) return 'empty';
    if (opts.reports === 0) return 'D0';
    if (opts.daysOfHistory >= 90) return 'D90';
    if (opts.daysOfHistory >= 30) return 'D30';
    if (opts.reports >= 3) return 'D7';
    return 'D0';
}

/** Index base: the mean of measured directions. Unmeasured ones do not vote. */
function baseIndex(scores: Array<number | null>): number | null {
    const got = scores.filter((s): s is number => s != null);

    if (!got.length) return null;
    return Math.round(got.reduce((a, b) => a + b, 0) / got.length);
}

export function useProfile(content: AppContent | null, userId: string | undefined, lang: Lang): Profile {
    const [state, setState] = React.useState<Profile>({ ...EMPTY, loading: true, error: null });

    React.useEffect(() => {
        if (!content) return undefined;
        if (!userId) {
            setState({ ...EMPTY, loading: false, error: null });
            return undefined;
        }
        let live = true;

        const run = async () => {
            try {
                // One row per pass, so a user who took three directions has three.
                const { data: rows, error } = await supabase
                    .from('results')
                    .select('answers, quest_name, created_at')
                    .eq('user_id', userId)
                    .order('created_at', { ascending: false })
                    .limit(50);

                if (error) throw error;
                if (!live) return;
                if (!rows || rows.length === 0) {
                    setState({ ...EMPTY, loading: false, error: null });
                    return;
                }

                // Newest row per track wins — a retake supersedes the earlier pass.
                const newest = new Map<string, Record<string, any>>();
                const newestAt = new Map<string, Date>();

                rows.forEach((row: any) => {
                    const a = (row.answers || {}) as Record<string, any>;
                    const track: string = a._track || a.selected_track || a._recommended_track;

                    if (track && !newest.has(track)) {
                        newest.set(track, a);
                        if (row.created_at) newestAt.set(track, new Date(row.created_at));
                    }
                });
                if (newest.size === 0) {
                    setState({ ...EMPTY, loading: false, error: null });
                    return;
                }

                // Local-first so scoring always uses the rules in this build,
                // matching how the legacy dashboard recomputes.
                const shell =
                    (await loadQuizPages({
                        quizName: rows[0].quest_name || 'doyourstep-quiz',
                        lang,
                        aliases: { 'doyourstep-quiz': 'doyourstep_v3' },
                    })) || [];

                const submissions: TrackSubmission[] = await Promise.all(
                    Array.from(newest).map(async ([track, answers]) => ({
                        track,
                        answers,
                        pages: [
                            ...shell,
                            ...((await loadQuizPages({ quizName: `doyourstep_track_${track}`, lang })) || []),
                        ],
                    })),
                );

                if (!live) return;

                const tagTypes: Record<string, Polarity> = {};
                const tagDimension: Record<string, string> = {};

                content.patterns.forEach((p) => {
                    tagTypes[p.code] = p.pol;
                    tagDimension[p.code] = p.dim;
                });

                const merged = mergeTrackResults(submissions, tagTypes, tagDimension);
                const pctBy: Record<string, number> = {};

                merged.scores.tags.forEach((t) => {
                    pctBy[t.id] = Math.round(t.percent);
                });

                const measured = new Set(merged.measuredTracks);
                const ranked = [...merged.measuredTracks].sort(
                    (a, b) => (merged.scores.dimensions[b]?.percent || 0) - (merged.scores.dimensions[a]?.percent || 0),
                );

                // Everything earned — points, reports, streak, maturity — is
                // merged in below, outside this effect: it comes from the task
                // log, which changes on a tap and must not wait for a refetch.
                const patterns: PatState[] = content.patterns.map((p) => ({
                    code: p.code,
                    name: p.name,
                    sub: p.sub,
                    pol: p.pol,
                    dim: p.dim,
                    pct: measured.has(p.dim) ? (pctBy[p.code] ?? null) : null,
                    earned: 0,
                    impact: null,
                    isNew: false,
                }));

                const directions: DirState[] = DIR_ORDER.map((code) => {
                    const deep = measured.has(code);
                    const score = deep ? Math.round(merged.scores.dimensions[code].percent) : null;
                    // Only patterns the user actually scored on belong to the
                    // direction's list; the rest of the catalogue is not "theirs".
                    // Every pattern the pass actually measured belongs here —
                    // including the ones that scored 0. A blocker at 0 is good
                    // news worth showing, and an ENGINE at 0 is the worst result
                    // in the model; filtering on `> 0` hid exactly those.
                    //
                    // Sorted by the engine-inverted value so "most in need of
                    // work first" agrees with the colours next to it.
                    const owned = patterns
                        .filter((p) => p.dim === code && p.pct != null)
                        .sort(
                            (a, b) =>
                                effectiveStrength(b.pct as number, b.pol) - effectiveStrength(a.pct as number, a.pol),
                        );

                    return {
                        code,
                        deep,
                        testedAt: newestAt.get(code) || null,
                        score,
                        rank: deep ? ranked.indexOf(code) + 1 : null,
                        patterns: owned,
                        earned: 0,
                        trend: [],
                        delta: null,
                    };
                });

                const base = baseIndex(directions.map((d) => d.score));

                setState({
                    ...EMPTY,
                    maturity: measured.size ? 'D0' : 'empty',
                    base,
                    index: base,
                    directions,
                    patterns,
                    measuredTracks: merged.measuredTracks,
                    testedAt: rows[0].created_at ? new Date(rows[0].created_at) : null,
                    loading: false,
                    error: null,
                });
            } catch (e: any) {
                if (live) setState({ ...EMPTY, loading: false, error: e instanceof Error ? e : new Error(String(e)) });
            }
        };

        run();
        return () => {
            live = false;
        };
    }, [content, userId, lang]);

    // Points come from the server task log (see `tasks.ts`) and change on a tap,
    // so they are folded in on every render rather than inside the fetch effect —
    // otherwise marking a day would leave the dashboard showing page-load totals.
    const { list } = useTasks();
    const earned = React.useMemo(() => earnedFrom(list), [list]);

    return React.useMemo(() => {
        const lv = levelOf(earned.total);

        return {
            ...state,
            dynamic: earned.total,
            reports: earned.reports,
            streak: earned.streak,
            week: earned.week,
            level: lv.level,
            xpToNext: lv.toNext,
            levelPct: lv.pct,
            maturity: maturityOf({
                measured: state.measuredTracks.length,
                reports: earned.reports,
                daysOfHistory: earned.daysOfHistory,
            }),
            directions: state.directions.map((d) => ({
                ...d,
                earned: earned.byDirection[d.code] || 0,
                trend: earned.weekByDirection[d.code] || [],
                patterns: d.patterns.map((p) => ({ ...p, earned: earned.byPattern[p.code] || 0 })),
            })),
            patterns: state.patterns.map((p) => ({ ...p, earned: earned.byPattern[p.code] || 0 })),
        };
    }, [state, earned]);
}
